import { jsx, jsxs } from "react/jsx-runtime";
import Badge from "@mui/material/Badge";
import styled from "@mui/material/styles/styled";
import MTab from "@mui/material/Tab";
import isUndefined from "lodash/isUndefined";
import { BADGE_SIZE, TABS_TOKENS } from "../../shared/constants";
import { useParentBg } from "../../shared/hooks/current-color";
import Box from "../Box";
import { Icon } from "../Icon";
var ETabSize = /* @__PURE__ */ ((ETabSize2) => {
  ETabSize2["L"] = "L";
  ETabSize2["M"] = "M";
  ETabSize2["S"] = "S";
  return ETabSize2;
})(ETabSize || {});
var ETabVariants = /* @__PURE__ */ ((ETabVariants2) => {
  ETabVariants2["HORIZONTAL"] = "horizontal";
  ETabVariants2["SHORT_HORIZONTAL"] = "shortHorizontal";
  ETabVariants2["VERTICAL"] = "vertical";
  ETabVariants2["SHORT_VERTICAL"] = "shortVertical";
  return ETabVariants2;
})(ETabVariants || {});
var EBadgeSize = /* @__PURE__ */ ((EBadgeSize2) => {
  EBadgeSize2["S"] = "s";
  EBadgeSize2["M"] = "m";
  return EBadgeSize2;
})(EBadgeSize || {});
const DEFAULT_BADGE_PROPS = {
  showBadge: false,
  bg: "brand",
  color: "contrast"
};
const FLEX_START = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  flexDirection: "row"
};
const FLEX_END = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end"
};
const FLEX_CENTER = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};
const INLINE_BADGE_POSITION = {
  position: "static !important",
  transition: "none !important",
  transform: "none !important"
};
const ICON_SIZE_MAPPER = {
  "horizontal": {
    size: {
      L: "l",
      M: "s",
      S: "s"
    }
  },
  "vertical": "l",
  "shortHorizontal": "s",
  "shortVertical": "xl"
};
const LABEL_CONTAINER_STYLES = {
  flexDirection: "row",
  sx: { maxWidth: "500px" }
};
const getFlexAlignment = (tabVariant) => {
  if (tabVariant === "vertical" /* VERTICAL */)
    return FLEX_START;
  if (tabVariant === "shortHorizontal" /* SHORT_HORIZONTAL */)
    return FLEX_END;
  if (tabVariant === "shortVertical" /* SHORT_VERTICAL */)
    return FLEX_CENTER;
};
const CustomTab = styled(MTab)((props) => {
  const { theme, size = "M" /* M */, tabVariant, selected } = props;
  const parentBg = useParentBg();
  const isSizeS = size === "S" /* S */;
  const isLongHorizontal = tabVariant === "horizontal" /* HORIZONTAL */;
  const isShortVertical = tabVariant === "shortVertical" /* SHORT_VERTICAL */;
  const showIndicator = isLongHorizontal && isSizeS;
  const palette = theme.palette;
  const variant = TABS_TOKENS[tabVariant];
  const typography = theme.typography[variant.typography[size]];
  const activeTabColor = showIndicator ? palette[parentBg].accent : palette[parentBg].contrast;
  const inactiveTabColor = palette[parentBg].lowContrast;
  const tabPaddingBottom = `${variant.padding.bottom[size]}px`;
  const tabSize = `${variant.size[size]}px`;
  const tabGap = `${variant.gap[size]}px`;
  const tabPadding = isShortVertical ? "0 2px" : 0;
  const maxWidth = TABS_TOKENS.maxWidth;
  return {
    ...typography,
    ...getFlexAlignment(tabVariant),
    minHeight: tabSize,
    minWidth: tabSize,
    maxWidth: `${maxWidth}px`,
    overflow: "hidden",
    padding: tabPadding,
    paddingBottom: tabPaddingBottom,
    gap: tabGap,
    color: inactiveTabColor,
    "&:hover": {
      color: activeTabColor
    },
    "&.Mui-selected": {
      color: activeTabColor
    },
    "& .MuiTab-iconWrapper": {
      marginRight: "0",
      marginBottom: "0"
    }
  };
});
const CustomBadge = styled(Badge)(({ theme, size, tabVariant, borderColor, position, bg = "brand", color = "contrast", showZero, badgeSize: hcBadgeSize }) => {
  const palette = theme.palette;
  const variant = TABS_TOKENS[tabVariant];
  const badgeBgColor = palette[bg].main;
  const badgeColor = palette[bg][color];
  const badgeTypography = theme.typography[variant.badge.typography[size]];
  const badgeSize = showZero ? `${BADGE_SIZE.s}px` : `${variant.badge.size[size]}px`;
  const badgeBorder = borderColor ? `${variant.badge.border[size]}px solid ${palette[borderColor].main}` : "";
  const badgePaddingLeft = `${variant.badge.padding[size]}px`;
  const zeroContentSX = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    position: "absolute",
    boxSizing: "border-box",
    lineHeight: 1,
    zIndex: 1,
    transition: "transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    borderRadius: "50%",
    height: badgeSize,
    minWidth: badgeSize,
    padding: 0,
    top: "14%",
    right: "14%",
    transform: "scale(1) translate(50%, -50%)",
    transformOrigin: "100% 0%"
  };
  return {
    paddingLeft: position ? badgePaddingLeft : "",
    "& .MuiBadge-badge": {
      color: badgeColor,
      height: badgeSize,
      ...showZero ? zeroContentSX : {},
      backgroundColor: badgeBgColor,
      border: badgeBorder,
      ...badgeTypography,
      ...position
    }
  };
});
const getIcon = (props) => {
  const { startIcon, tabVariant, counter, tabIconSize, showBadge } = props;
  const defaultTabVariant = tabVariant === "horizontal" /* HORIZONTAL */ || tabVariant === "vertical" /* VERTICAL */;
  const isShort = tabVariant === "shortHorizontal" /* SHORT_HORIZONTAL */ || "shortVertical" /* SHORT_VERTICAL */;
  if (!startIcon)
    return {};
  if (defaultTabVariant)
    return {
      icon: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Icon, { icon: startIcon, size: tabIconSize }) }),
      iconPosition: "start"
    };
  if (isShort && (counter || showBadge))
    return {
      icon: /* @__PURE__ */ jsx(CustomBadge, { badgeContent: counter || showBadge && " ", max: 99, showZero: !counter && showBadge, ...props, children: /* @__PURE__ */ jsx(Icon, { icon: startIcon, size: tabIconSize }) })
    };
  if (isShort && !counter)
    return {
      icon: /* @__PURE__ */ jsx(Icon, { icon: startIcon, size: tabIconSize })
    };
};
const getLabel = (props) => {
  const { counter, tabVariant, label, showBadge } = props;
  const shortVertical = tabVariant === "shortVertical" /* SHORT_VERTICAL */;
  const shortHorizontal = tabVariant === "shortHorizontal" /* SHORT_HORIZONTAL */;
  const isShort = shortHorizontal || shortVertical;
  if (isUndefined(counter) && !showBadge || isShort)
    return label;
  return /* @__PURE__ */ jsxs(Box, { ...LABEL_CONTAINER_STYLES, children: [
    label,
    /* @__PURE__ */ jsx(
      CustomBadge,
      {
        badgeContent: counter,
        showZero: showBadge && counter <= 0,
        max: 99,
        position: INLINE_BADGE_POSITION,
        ...props
      }
    )
  ] });
};
const Tab = (props) => {
  const {
    startIcon,
    counter,
    label,
    size = "L" /* L */,
    tabVariant = "horizontal" /* HORIZONTAL */,
    badgeProps = { ...DEFAULT_BADGE_PROPS },
    borderColor,
    ...otherProps
  } = props;
  const { bg, color, size: badgeSize, showBorder } = badgeProps;
  const showBadge = badgeProps.showBadge || counter > 0;
  const tabIconSize = tabVariant === "horizontal" /* HORIZONTAL */ ? ICON_SIZE_MAPPER.horizontal.size[size] : ICON_SIZE_MAPPER[tabVariant];
  const iconProps = getIcon({
    startIcon,
    tabVariant,
    counter,
    size,
    tabIconSize,
    showBadge,
    bg,
    color,
    badgeSize,
    borderColor
  });
  const tabLabel = getLabel({
    counter,
    label,
    size,
    tabVariant,
    borderColor: void 0,
    showBadge,
    bg,
    color,
    badgeSize
  });
  return /* @__PURE__ */ jsx(
    CustomTab,
    {
      startIcon,
      counter,
      size,
      tabVariant,
      ...otherProps,
      ...iconProps,
      label: tabLabel
    }
  );
};
var Tab_default = Tab;
export {
  EBadgeSize,
  ETabSize,
  ETabVariants,
  Tab_default as default
};
